<template>
  <div
    class="cart__product-recommendation-items pb-sm"
    v-if="loading || cartProductRecommendation?.length"
  >
    <h2 class="text--center cart__product-recommendation-title mt-0">
      You may also like
    </h2>
    <ProductListSlider
      @item-click="closeCartPopup"
      :key="cartProductRecommendation"
      :products="
        cartProductRecommendation?.length && !loading
          ? cartProductRecommendation
          : placeholderProducts
      "
    />
  </div>
</template>

<script lang="ts" setup>
import { onMounted, onUnmounted, computed, ref, watch } from 'vue'
import useProductStore from '@/store/product'
import ProductListSlider from '@/components/product/ProductListSlider.vue'
import LocationService, { UserLocation } from '@/services/location'
import { Recommendation } from '@/types'
import {
  EVENT_SHOW_CART_POPUP,
  EVENT_CLOSE_CART_POPUP,
  QUERY_STRING_NO_CACHE,
} from '@/utils'
import EventBus from '@/services/eventbus'
import useUserStore from '@/store/user'
import { useRoute } from 'vue-router'
import useCartStore from '@/store/cart'
import { useProductRecentlyViewedItems } from '@/pages/product/composables/ProductRecentlyViewed'

const route = useRoute()
const userStore = useUserStore()
const productStore = useProductStore()
const cartStore = useCartStore()
const { getRecentlyViewedItems } = useProductRecentlyViewedItems()

const loading = ref(false)
const placeholderProducts = Array(10).fill(undefined)

const cartProductRecommendation = computed(() => {
  return productStore.productRecommendation?.moreItems?.length
    ? productStore.productRecommendation?.moreItems
    : productStore.productRecommendationShopify
})

onMounted(async () => {
  EventBus.on(EVENT_SHOW_CART_POPUP, getProductRecommendation)

  if (route.path == '/cart') {
    watch(
      () => cartStore.cart?.id,
      () => {
        if (cartStore.cart?.lines?.length) getProductRecommendation()
      },
      {
        immediate: true,
      }
    )
  }
})

onUnmounted(() => {
  EventBus.off(EVENT_SHOW_CART_POPUP, getProductRecommendation)
  if (route.path == '/cart') {
    watch(
      () => cartStore.cart?.id,
      () => {
        if (cartStore.cart?.lines?.length) getProductRecommendation()
      },
      {
        immediate: true,
      }
    )
  }
})

function getProductRecommendation() {
  if (
    productStore.productRecommendation?.moreItems?.length ||
    productStore.productRecommendationShopify?.length
  ) {
    return
  }
  loading.value = true
  LocationService.onLocationReady(async (location: UserLocation | null) => {
    const countryCode = location?.country_code
    const recentlyViewedProducts = await getRecentlyViewedItems()

    let product = productStore.product

    if (!product) {
      product = cartStore.cart?.lines[0]?.merchandise?.product
    }
    if (!product && recentlyViewedProducts?.length) {
      product = recentlyViewedProducts[0]
    }
    if (!product) return (loading.value = false)

    const recomendationPayload: Recommendation = {
      recently_view: recentlyViewedProducts.map((product) => product.handle),
      product: {
        title: product.title,
        handle: product.handle,
        tags: product.tags || [],
        type: product.productType || '',
      },
      location: countryCode,
      user_segments: userStore.segments
        ? userStore.segments.map((segment) => segment.key)
        : [],
    }
    await productStore.getProductRecommendation(recomendationPayload)

    if (!productStore.productRecommendation?.moreItems?.length && product.id) {
      const isForceLoadNewData = !!route.query[QUERY_STRING_NO_CACHE]

      await productStore.getProductRecommendationsShopify({
        id: product.id,
        isForceLoadNewData,
      })
    }
    loading.value = false
  })
}

function closeCartPopup() {
  EventBus.trigger(EVENT_CLOSE_CART_POPUP)
}
</script>

<style lang="scss">
.cart__product-recommendation-items {
  grid-area: c;
  .cart__product-recommendation-title {
    font-size: 20px;
  }
}
.cart-popup {
  .carousel {
    --carousel-slide-width: calc((100% - 1em) / 2 - 1px);
  }
}
</style>
